import type { PcCommunication, PcPatientCommsBundle } from './types';

const AMARA_COMMS: PcCommunication[] = [
  {
    id: 'c1',
    channel: 'recall',
    direction: 'out',
    group: 'This week',
    title: '6-month hygiene recall',
    time: '3 days ago',
    preview:
      "Multi-touch campaign across text and email to book Amara's overdue hygiene visit.",
    status: ['delivered', 'booked'],
    outcome: { label: 'Booked — May 28 cleaning', tone: 'positive' },
    detail: {
      campaign: '6-Month Hygiene Recall',
      touchpoints: [
        {
          ch: 'sms',
          note: 'Initial text — "Hi Amara, you\'re due for your cleaning…"',
          result: 'Read · No reply',
          time: 'May 18',
        },
        {
          ch: 'email',
          note: 'Follow-up email with online booking link',
          result: 'Clicked book',
          time: 'May 20',
        },
        { ch: 'sms', note: 'Booking confirmation', result: 'Read', time: 'May 21' },
      ],
    },
  },
  {
    id: 'c2',
    channel: 'reminder',
    direction: 'out',
    group: 'This week',
    title: 'Appointment reminder · Cleaning',
    time: '3 days ago',
    preview: 'Reminder for the May 28 cleaning with Dr. Carter. Reply C to confirm.',
    status: ['delivered', 'read', 'confirmed'],
    outcome: { label: 'Confirmed via text', tone: 'positive' },
    detail: {
      appointment: 'Hygiene + perio eval · May 28, 2:30 PM · Dr. Carter',
      sentVia: 'Text',
      body: 'Hi Amara! This is a reminder for your visit with Dr. Carter on Wednesday, May 28 at 2:30 PM. Reply C to confirm or call us to reschedule.',
      reply: { text: 'C', time: '8 min later' },
    },
  },
  {
    id: 'c3',
    channel: 'survey',
    direction: 'out',
    group: 'This month',
    title: 'Post-treatment satisfaction',
    time: '2 weeks ago',
    preview: '5-question check-in sent after Amara\'s crown placement.',
    status: ['delivered', 'completed'],
    outcome: { label: 'Completed · 5/5 satisfaction', tone: 'positive' },
    detail: {
      surveyName: 'Post-Treatment Satisfaction',
      completedIn: '3 min',
      score: '5 / 5',
      reportUrl: true,
    },
  },
  {
    id: 'c4',
    channel: 'call',
    direction: 'in',
    group: 'This month',
    title: 'Inbound call · Whitening questions',
    time: '2 weeks ago',
    preview: 'Amara called about whitening before her family wedding. ELVA handled scheduling.',
    status: ['answered', 'ai_handled'],
    outcome: { label: 'Consult booked — Jun 3', tone: 'positive' },
    detail: {
      duration: '4m 12s',
      handledBy: 'ELVA AI',
      aiSummary:
        'Amara called about in-office whitening before an October wedding. ELVA explained options and pricing, noted her interest from the April hygiene visit, and offered a consult slot. Booked Jun 3 at 11:00 AM with Dr. Carter.',
      hasRecording: true,
    },
  },
  {
    id: 'c5',
    channel: 'email',
    direction: 'out',
    group: 'This month',
    title: 'Your treatment plan is ready',
    time: '3 weeks ago',
    preview: 'Secure link to review perio and whitening options with cost estimate.',
    status: ['delivered', 'clicked'],
    outcome: { label: 'Clicked link', tone: 'neutral' },
    detail: {
      subject: 'Your treatment plan is ready to review, Amara',
      body: 'Hi Amara,\n\nDr. Carter has prepared your treatment plan following your recent visit. You can review recommended care, see your estimated cost with insurance, and ask questions through the secure link below.\n\nReview your plan →',
      clicked: 'May 9, 8:03 AM',
    },
  },
  {
    id: 'c6',
    channel: 'reminder',
    direction: 'out',
    group: 'This month',
    title: 'Appointment reminder · Crown',
    time: '3 weeks ago',
    preview: 'Reminder for the Oct 14 crown with Dr. Carter.',
    status: ['delivered', 'read', 'confirmed'],
    outcome: { label: 'Confirmed', tone: 'positive' },
    detail: {
      appointment: 'Crown · #14 · Oct 14, 2:00 PM · Dr. Carter',
      sentVia: 'Text, then Email',
      body: 'Reminder: your crown appointment with Dr. Carter is Oct 14 at 2:00 PM. Please arrive 10 minutes early. Reply C to confirm.',
      reply: { text: 'C — see you then', time: '1 hour later' },
    },
  },
  {
    id: 'c7',
    channel: 'sms',
    direction: 'out',
    group: 'Earlier',
    title: 'Balance reminder',
    time: '1 month ago',
    preview: 'Gentle reminder of a $340 balance with a secure pay link.',
    status: ['delivered', 'read', 'replied'],
    outcome: { label: 'Replied — paid online next day', tone: 'positive' },
    detail: {
      body: 'Hi Amara, just a friendly reminder you have a $340 balance. You can pay securely here: [link]. Let us know if you have any questions!',
      reply: { text: 'Thanks — will pay online tonight', time: '2 hours later' },
    },
  },
  {
    id: 'c8',
    channel: 'email',
    direction: 'out',
    group: 'Earlier',
    title: 'Spring whitening special',
    time: '1 month ago',
    preview: 'Seasonal marketing email promoting the whitening package.',
    status: ['delivered'],
    outcome: { label: 'Delivered · no clicks', tone: 'muted' },
    detail: {
      subject: 'Brighten your smile this spring',
      campaign: 'Spring Cosmetic Promo',
      body: 'Limited-time offer on professional whitening…',
      note: 'Marketing email — Amara has opted in to promotional emails.',
    },
  },
  {
    id: 'c9',
    channel: 'recall',
    direction: 'out',
    group: 'Earlier',
    title: 'Overdue hygiene reactivation',
    time: '2 months ago',
    preview: 'Reactivation campaign for a hygiene visit that was 2 months overdue.',
    status: ['delivered', 'booked'],
    outcome: { label: 'Booked — visit completed Apr 23', tone: 'positive' },
    detail: {
      campaign: 'Overdue Hygiene Reactivation',
      touchpoints: [
        {
          ch: 'sms',
          note: 'Reactivation text with booking link',
          result: 'Read · Clicked',
          time: 'Mar 30',
        },
        { ch: 'sms', note: 'Confirmation', result: 'Read', time: 'Mar 31' },
      ],
    },
  },
  {
    id: 'c10',
    channel: 'call',
    direction: 'out',
    group: 'Earlier',
    title: 'Outbound confirmation call',
    time: '2 months ago',
    preview: 'ELVA called to confirm an upcoming appointment.',
    status: ['no_answer', 'voicemail'],
    outcome: { label: 'Left voicemail', tone: 'muted' },
    detail: {
      duration: '0m 38s',
      handledBy: 'ELVA AI',
      aiSummary:
        'Outbound confirmation call for the Apr 2 appointment. No answer — ELVA left a voicemail and followed up with a text, which Amara confirmed.',
      hasRecording: true,
    },
  },
];

const AMARA_BUNDLE: PcPatientCommsBundle = {
  aiSummary:
    "Over the past 12 months, Amara has received 24 communications across 5 channels. She's most responsive to text — she reads nearly every message and replies to about half within 2 hours. Both recall campaigns sent to her converted to booked appointments, and she completed both satisfaction surveys. Her last contact was 3 days ago, confirming her May 28 hygiene visit.",
  stats: [
    { channel: 'email', count: 8, sub: '2 clicked' },
    { channel: 'sms', count: 9, sub: '8 read · 5 replied' },
    { channel: 'call', count: 3, sub: '2 answered' },
    { channel: 'survey', count: 2, sub: '2 completed' },
    { channel: 'recall', count: 2, sub: '2 booked' },
    { channel: 'reminder', count: 6, sub: '5 confirmed' },
  ],
  communications: AMARA_COMMS,
  meta: {
    preferredName: 'Amara',
    pronouns: 'She/Her',
    preferredContact: 'Text',
  },
};

const JAMES_BUNDLE: PcPatientCommsBundle = {
  aiSummary:
    'James has had 11 communications in the last year, mostly text and email around Invisalign. He responds quickly to texts and clicked both treatment-plan emails. Last contact was 5 days ago — appointment reminder for Jun 6.',
  stats: [
    { channel: 'email', count: 4, sub: '2 clicked' },
    { channel: 'sms', count: 5, sub: '5 read · 3 replied' },
    { channel: 'call', count: 1, sub: '1 answered' },
    { channel: 'reminder', count: 3, sub: '3 confirmed' },
  ],
  communications: AMARA_COMMS.slice(4, 8).map((c, i) => ({
    ...c,
    id: `j${i}`,
    preview: c.preview.replace(/Amara/g, 'James'),
    group: i < 2 ? 'This month' : 'Earlier',
  })),
  meta: { preferredName: 'James', pronouns: 'He/Him', preferredContact: 'Text' },
};

const SARA_BUNDLE: PcPatientCommsBundle = {
  aiSummary:
    'Sara is a new patient with 6 communications since May. She confirmed her first visit by text and completed the new-patient forms email. Most engagement is email and text around whitening.',
  stats: [
    { channel: 'email', count: 3, sub: '2 clicked' },
    { channel: 'sms', count: 2, sub: '2 read' },
    { channel: 'reminder', count: 1, sub: '1 confirmed' },
  ],
  communications: AMARA_COMMS.slice(1, 5).map((c, i) => ({
    ...c,
    id: `s${i}`,
    preview: c.preview.replace(/Amara/g, 'Sara'),
    group: 'This month',
  })),
  meta: { preferredName: 'Sara', pronouns: 'She/Her', preferredContact: 'Email' },
};

const TOM_BUNDLE: PcPatientCommsBundle = {
  aiSummary:
    'Tom has 18 communications over 12 months but lower text engagement. Recall campaigns eventually booked visits; last recall outreach was 3 weeks ago with no booking yet. Prefers phone when he does respond.',
  stats: [
    { channel: 'email', count: 5, sub: '1 clicked' },
    { channel: 'sms', count: 4, sub: '2 read' },
    { channel: 'call', count: 4, sub: '3 answered' },
    { channel: 'recall', count: 3, sub: '2 booked' },
  ],
  communications: AMARA_COMMS.filter((c) =>
    ['recall', 'call', 'sms'].includes(c.channel),
  ).map((c, i) => ({
    ...c,
    id: `t${i}`,
    preview: c.preview.replace(/Amara/g, 'Tom'),
    group: i < 2 ? 'This week' : 'Earlier',
  })),
  meta: { preferredName: 'Tom', pronouns: 'He/Him', preferredContact: 'Phone' },
};

export const PP_COMMS_BY_PATIENT: Record<string, PcPatientCommsBundle> = {
  'amara-reza': AMARA_BUNDLE,
  'james-chen': JAMES_BUNDLE,
  'sara-malik': SARA_BUNDLE,
  'tom-rivera': TOM_BUNDLE,
};
